import { getCardImage } from "@dm-online/shared";

export interface DeckImageInput {
  name: string;
  /** メイン（山札）の刷りID。並び順のまま1枚ずつ描画する */
  mainIds: string[];
  /** 超次元ゾーンの刷りID */
  hyperIds: string[];
}

const COLS = 8;
const CARD_W = 220;
const CARD_H = Math.round((CARD_W * 88) / 63);
const GAP = 8;
const PAD = 32;
const HEADER_H = 72;
const SECTION_H = 40;
const BG = "#14161c";
const FG = "#f3f4f6";
const MUTED = "#9ca3af";
const FONT = '"Hiragino Sans", "Noto Sans JP", "Yu Gothic", system-ui, sans-serif';

async function loadImage(url: string): Promise<ImageBitmap | null> {
  try {
    // サムネイル表示用にCORSヘッダ無しでキャッシュされている可能性があるので再検証する
    const res = await fetch(url, { mode: "cors", cache: "no-cache" });
    if (!res.ok) return null;
    return await createImageBitmap(await res.blob());
  } catch {
    return null;
  }
}

function gridHeight(count: number): number {
  const rows = Math.ceil(count / COLS);
  return rows * CARD_H + Math.max(0, rows - 1) * GAP;
}

/** デッキを1枚の画像（PNG Blob）として描画する */
export async function renderDeckImage(input: DeckImageInput): Promise<Blob> {
  const { name, mainIds, hyperIds } = input;
  const urls = [...new Set([...mainIds, ...hyperIds])];
  const bitmaps = new Map<string, ImageBitmap | null>();
  await Promise.all(
    urls.map(async (id) => {
      const url = getCardImage(id);
      bitmaps.set(id, url ? await loadImage(url) : null);
    }),
  );

  const width = PAD * 2 + COLS * CARD_W + (COLS - 1) * GAP;
  let height = PAD + HEADER_H + gridHeight(mainIds.length) + PAD;
  if (hyperIds.length > 0) height += SECTION_H + gridHeight(hyperIds.length) + 8;

  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d")!;
  ctx.fillStyle = BG;
  ctx.fillRect(0, 0, width, height);

  ctx.textBaseline = "alphabetic";
  ctx.fillStyle = FG;
  ctx.font = `bold 44px ${FONT}`;
  ctx.fillText(name || "無題のデッキ", PAD, PAD + 44, width - PAD * 2);

  function drawGrid(ids: string[], top: number) {
    ids.forEach((id, i) => {
      const x = PAD + (i % COLS) * (CARD_W + GAP);
      const y = top + Math.floor(i / COLS) * (CARD_H + GAP);
      const bmp = bitmaps.get(id);
      ctx.save();
      ctx.beginPath();
      ctx.roundRect(x, y, CARD_W, CARD_H, 8);
      ctx.clip();
      if (bmp) {
        ctx.drawImage(bmp, x, y, CARD_W, CARD_H);
      } else {
        ctx.fillStyle = "#2a2e38";
        ctx.fillRect(x, y, CARD_W, CARD_H);
      }
      ctx.restore();
    });
  }

  let y = PAD + HEADER_H;
  drawGrid(mainIds, y);
  y += gridHeight(mainIds.length);
  if (hyperIds.length > 0) {
    y += 8;
    ctx.fillStyle = MUTED;
    ctx.font = `bold 24px ${FONT}`;
    ctx.fillText(`超次元ゾーン（${hyperIds.length}）`, PAD, y + 28);
    drawGrid(hyperIds, y + SECTION_H);
  }

  const missing = [...bitmaps.values()].filter((b) => !b).length;
  if (missing === bitmaps.size && bitmaps.size > 0) {
    throw new Error("カード画像を取得できませんでした");
  }
  return await new Promise<Blob>((resolve, reject) =>
    canvas.toBlob((b) => (b ? resolve(b) : reject(new Error("画像の生成に失敗しました"))), "image/png"),
  );
}
